export type PackageEcosystem = 'npm' | 'nuget';
export type PackageListView = 'freshness' | 'vulnerabilities';

export const PACKAGE_LIST_FILTERS = [
  'outdated',
  'majorBehind',
  'deprecated',
  'unresolved',
  'gap',
  'critical',
  'high',
  'medium',
  'low',
  'fixable',
] as const;
export type PackageListFilter = (typeof PACKAGE_LIST_FILTERS)[number];

/** At most this many rows are returned; `count` is still the true number. */
export const PACKAGE_LIST_MAX_ROWS = 500;

/** What the rows of a list have in common, for the caption. */
export const PACKAGE_FILTER_LABELS: Record<PackageListFilter, string> = {
  outdated: 'Outdated direct packages',
  majorBehind: 'Direct packages at least one major version behind',
  deprecated: 'Deprecated direct packages',
  unresolved: 'Direct packages that could not be checked',
  gap: 'Outdated direct packages with a known publish gap',
  critical: 'Packages whose worst advisory is critical',
  high: 'Packages whose worst advisory is high',
  medium: 'Packages whose worst advisory is medium',
  low: 'Packages whose worst advisory is low',
  fixable: 'Vulnerable packages with a fix available',
};

export interface PackageListSpec {
  view: PackageListView;
  filter: PackageListFilter;
  ecosystems: PackageEcosystem[];
}

const FRESHNESS_FILTERS: Record<string, PackageListFilter> = {
  'outdated.count': 'outdated',
  'outdated.percent': 'outdated',
  'majorBehind.count': 'majorBehind',
  'deprecated.count': 'deprecated',
  'unresolved.count': 'unresolved',
  libyears: 'gap',
  oldestGapDays: 'gap',
};

const PER_ECOSYSTEM = /^packages\.(npm|nuget)\.(.+)$/;
const VULNERABILITY = /^vulnerabilities\.(critical|high|medium|low|fixable)$/;
const COMBINED = /^packages\.vulnerabilities\.(critical|high)$/;

/**
 * The package list a fact opens, or undefined when the fact has none
 * (inventory facts, anything that is not a package fact).
 */
export function packageListFor(factKey: string): PackageListSpec | undefined {
  const perEcosystem = PER_ECOSYSTEM.exec(factKey);
  if (perEcosystem) {
    const ecosystem = perEcosystem[1] as PackageEcosystem;
    const rest = perEcosystem[2];
    if (Object.prototype.hasOwnProperty.call(FRESHNESS_FILTERS, rest)) {
      return {
        view: 'freshness',
        filter: FRESHNESS_FILTERS[rest],
        ecosystems: [ecosystem],
      };
    }
    const vulnerability = VULNERABILITY.exec(rest);
    return vulnerability
      ? {
          view: 'vulnerabilities',
          filter: vulnerability[1] as PackageListFilter,
          ecosystems: [ecosystem],
        }
      : undefined;
  }
  const combined = COMBINED.exec(factKey);
  return combined
    ? {
        view: 'vulnerabilities',
        filter: combined[1] as PackageListFilter,
        ecosystems: ['npm', 'nuget'],
      }
    : undefined;
}

/**
 * True when the fact's value is a number of packages, so it can be compared
 * with the number of rows of its list (percentages, libyears and day counts
 * cannot).
 */
export function isCountComparable(factKey: string): boolean {
  return (
    packageListFor(factKey) !== undefined &&
    !/\.(percent|libyears|oldestGapDays)$/.test(factKey)
  );
}

export type UnresolvedReason =
  | 'noVersion'
  | 'unknownPackage'
  | 'versionNotListed'
  | 'noStableLatest'
  | 'notChecked';

export interface FreshnessRow {
  ecosystem: PackageEcosystem;
  name: string;
  current?: string;
  latest?: string;
  currentPublishedAt?: string;
  latestPublishedAt?: string;
  /** Days between the publish dates of the current and the latest version. */
  gapDays?: number;
  outdated: boolean;
  majorBehind: boolean;
  deprecated: boolean;
  /** Why it could not be checked; absent for a checked package. */
  unresolved?: UnresolvedReason;
}

export interface VulnerabilityRow {
  ecosystem: PackageEcosystem;
  name: string;
  version: string;
  direct: boolean;
  severity: 'critical' | 'high' | 'medium' | 'low';
  fixable: boolean;
  /** Withdrawn advisories are left out. */
  advisories: Array<{ id: string; severity?: string; fixable?: boolean }>;
  /** Some advisory is not in the cache any more: its id is listed without details. */
  detailsMissing?: boolean;
}

interface PackageListBase {
  fact: string;
  filter: PackageListFilter;
  ecosystems: PackageEcosystem[];
  /** null when the repository has never been scanned. */
  scan: { scannedAt: string; complete: boolean } | null;
  /** Packages the stored data counts for this fact; compare with the rule's value. */
  count: number;
  /** True when `rows` holds only the first PACKAGE_LIST_MAX_ROWS of `count`. */
  truncated: boolean;
}

export type PackageListResponse =
  | (PackageListBase & { view: 'freshness'; rows: FreshnessRow[] })
  | (PackageListBase & {
      view: 'vulnerabilities';
      rows: VulnerabilityRow[];
      /**
       * Packages of the scanned inventory with no stored vulnerability data (not
       * collected yet, or pruned). They are not in `rows`; 0 when never scanned.
       */
      unchecked: number;
    });
